import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Send, CheckCircle2, AlertCircle, Clock, Calendar } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { fmtDate } from '../lib/format';
import { toast } from 'sonner';
import { TimezoneSelect } from '../components/TimezoneSelect';
import { DEFAULT_TIMEZONE } from '../lib/timezones';
import TurnstileWidget, { turnstileConfigured } from '../components/TurnstileWidget';

export default function BookIntakePage() {
  const { typeId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [type, setType] = useState(null);
  const [draft, setDraft] = useState(null);
  const [form, setForm] = useState({
    client_first_name: '', client_last_name: '', client_email: '',
    client_phone: '', client_company: '', notes: '', consent: false,
    timezone: (typeof Intl !== 'undefined' && Intl.DateTimeFormat().resolvedOptions().timeZone) || DEFAULT_TIMEZONE,
  });
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [tsToken, setTsToken] = useState('');
  const captchaRef = useRef(null);
  const needCaptcha = turnstileConfigured && !user;

  useEffect(() => {
    const raw = sessionStorage.getItem('wyse_booking_draft');
    if (!raw) { nav(`/book/${typeId}`); return; }
    const d = JSON.parse(raw);
    if (d.typeId !== typeId) { nav(`/book/${typeId}`); return; }
    setDraft(d);
    api.listTypes().then((all) => setType(all.find((x) => x.id === typeId)));
  }, [typeId, nav]);

  // Pre-fill from logged in user
  useEffect(() => {
    if (!user) return;
    const [first, ...rest] = (user.name || '').split(' ');
    setForm((f) => ({
      ...f,
      client_first_name: f.client_first_name || first || '',
      client_last_name: f.client_last_name || rest.join(' ') || '',
      client_email: f.client_email || user.email || '',
      client_phone: f.client_phone || user.phone || '',
    }));
  }, [user]);

  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_first_name || !form.client_last_name || !form.client_email) {
      toast.error('Please fill in your name and email');
      return;
    }
    if (!form.consent) {
      toast.error('Please accept the consent checkbox');
      return;
    }
    if (needCaptcha && !tsToken) {
      toast.error('Please complete the verification challenge');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        appointment_type_id: typeId,
        advisor_id: draft.advisorId,
        start: draft.start,
        ...form,
        intake_answers: answers,
        timezone: form.timezone || DEFAULT_TIMEZONE,
      };
      const res = await api.createBooking(payload, tsToken);
      sessionStorage.removeItem('wyse_booking_draft');
      nav(`/book/confirm/${res.token}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Booking failed. Please try again.';
      toast.error(msg);
      setTsToken(''); captchaRef.current?.reset();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Header />
      <section className="py-16 bg-white">
        <div className="max-w-[1100px] mx-auto px-6">
          <Link to={`/book/${typeId}`} className="inline-flex items-center gap-1.5 text-[13.5px] text-neutral-500 hover:text-[#c89200] mb-5">
            <ArrowLeft className="w-4 h-4" /> Back to time selection
          </Link>
          <p className="section-eyebrow mb-2">Step 3 of 3</p>
          <h1 className="text-[32px] md:text-[40px] font-extrabold text-[#14110f]">Tell us about you</h1>

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-7">
            <form onSubmit={submit} className="lg:col-span-7 rounded-xl border border-neutral-100 p-7 bg-white shadow-[0_20px_45px_-25px_rgba(20,17,15,0.18)] space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input name="client_first_name" value={form.client_first_name} onChange={handle} placeholder="First name *" className="px-4 py-3.5 rounded-md bg-white border border-neutral-200 focus:border-[#f5b300] focus:ring-2 focus:ring-[#f5b300]/20 outline-none text-[14px]" />
                <input name="client_last_name" value={form.client_last_name} onChange={handle} placeholder="Last name *" className="px-4 py-3.5 rounded-md bg-white border border-neutral-200 focus:border-[#f5b300] focus:ring-2 focus:ring-[#f5b300]/20 outline-none text-[14px]" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input name="client_email" type="email" value={form.client_email} onChange={handle} placeholder="Email *" className="px-4 py-3.5 rounded-md bg-white border border-neutral-200 focus:border-[#f5b300] focus:ring-2 focus:ring-[#f5b300]/20 outline-none text-[14px]" />
                <input name="client_phone" value={form.client_phone} onChange={handle} placeholder="Phone" className="px-4 py-3.5 rounded-md bg-white border border-neutral-200 focus:border-[#f5b300] focus:ring-2 focus:ring-[#f5b300]/20 outline-none text-[14px]" />
              </div>
              <input name="client_company" value={form.client_company} onChange={handle} placeholder="Company (optional)" className="w-full px-4 py-3.5 rounded-md bg-white border border-neutral-200 focus:border-[#f5b300] focus:ring-2 focus:ring-[#f5b300]/20 outline-none text-[14px]" />
              <label className="block">
                <span className="text-[12px] uppercase tracking-wider font-semibold text-neutral-500">Your timezone</span>
                <div className="mt-1.5">
                  <TimezoneSelect value={form.timezone}
                                  onChange={(v) => setForm((f) => ({ ...f, timezone: v }))}
                                  dataTestid="intake-timezone"
                                  className="w-full px-4 py-3.5 rounded-md bg-white border border-neutral-200 focus:border-[#f5b300] focus:ring-2 focus:ring-[#f5b300]/20 outline-none text-[14px]" />
                </div>
                <span className="block mt-1 text-[11.5px] text-neutral-400">We&rsquo;ll send your reminders and meeting link in this zone.</span>
              </label>
              <textarea name="notes" value={form.notes} onChange={handle} rows={4} placeholder="Notes or reason for the appointment" className="w-full px-4 py-3.5 rounded-md bg-white border border-neutral-200 focus:border-[#f5b300] focus:ring-2 focus:ring-[#f5b300]/20 outline-none text-[14px] resize-none" />

              {type?.intake_questions?.length > 0 && (
                <div className="pt-3 border-t border-neutral-100 space-y-3">
                  <p className="text-[12px] uppercase tracking-wider font-bold text-[#c89200]">A few quick questions</p>
                  {type.intake_questions.map((q, i) => (
                    <div key={i}>
                      <label className="block text-[13px] font-medium text-[#14110f] mb-1">{q}</label>
                      <input value={answers[q] || ''} onChange={(e) => setAnswers({ ...answers, [q]: e.target.value })} className="w-full px-4 py-3 rounded-md bg-white border border-neutral-200 focus:border-[#f5b300] focus:ring-2 focus:ring-[#f5b300]/20 outline-none text-[14px]" />
                    </div>
                  ))}
                </div>
              )}

              <label className="flex items-start gap-3 cursor-pointer pt-2">
                <input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-1 w-4 h-4 accent-[#f5b300]" />
                <span className="text-[13px] text-neutral-600 leading-relaxed">
                  I agree to the WYSE Tribe <a href="#" className="text-[#c89200] underline">privacy policy</a> and consent to receive booking-related emails.
                </span>
              </label>

              {needCaptcha && (
                <TurnstileWidget ref={captchaRef}
                                 onVerify={setTsToken}
                                 onExpire={() => setTsToken('')}
                                 testId="booking-turnstile" />
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#f5b300] hover:bg-[#e0a200] disabled:opacity-60 text-[#14110f] font-semibold px-7 py-4 rounded-md shadow-[0_14px_30px_-10px_rgba(245,179,0,0.7)] hover:-translate-y-0.5 transition-all"
              >
                {submitting ? 'Booking…' : (<><CheckCircle2 className="w-4 h-4" /> Confirm booking</>)}
              </button>
            </form>

            <aside className="lg:col-span-5">
              <div className="rounded-xl border border-neutral-100 p-6 bg-[#faf6ec] sticky top-32">
                <p className="text-[12px] uppercase tracking-wider font-bold text-[#c89200] mb-4">Booking summary</p>
                {type && (
                  <>
                    <p className="text-[18px] font-bold text-[#14110f]">{type.name}</p>
                    <p className="text-[13px] text-neutral-600 mt-1">{type.description}</p>
                    {draft && (
                      <div className="mt-5 space-y-2.5 text-[14px]">
                        <p className="flex items-center gap-2 text-[#14110f]"><Calendar className="w-4 h-4 text-[#c89200]" /> {fmtDate(draft.start)}</p>
                        <p className="flex items-center gap-2 text-[#14110f]"><Clock className="w-4 h-4 text-[#c89200]" /> {type.duration_minutes} minutes</p>
                      </div>
                    )}
                    <div className="mt-5 pt-5 border-t border-[#e6ddc6]">
                      <div className="flex items-center justify-between">
                        <span className="text-[13px] text-neutral-600">Total</span>
                        <span className="text-[18px] font-extrabold text-[#14110f]">{type.price > 0 ? `$${type.price}` : 'Free'}</span>
                      </div>
                    </div>
                    <p className="mt-5 text-[12px] text-neutral-500 flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-[#c89200] mt-0.5 shrink-0" />
                      Free reschedule or cancel up to 12 hours before your appointment.
                    </p>
                  </>
                )}
              </div>
            </aside>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
}
